import axios from "axios";

import { getAccessToken } from "../../../shared/auth/authSession";
import { env } from "../../../shared/config/env";
import { normalizeApiError } from "../../../shared/errors/normalizeApiError";

export type ProposeTemplateStatus = -1 | 0 | 1;

export type ProposeTemplate = {
  id: number;
  name: string;
  status: ProposeTemplateStatus;
};

export type ProposeTemplateStatusResponse = {
  status: ProposeTemplateStatus;
  error?: string;
};

export type ProposeTemplateField = {
  id: string;
  label: string;
  defaultValue?: string | number | boolean | null;
  inputType?: string;
  required?: boolean;
  readOnly?: boolean;
  calculated?: boolean;
  headerFieldId?: string | null;
  groupId?: string;
  editableBy?: string;
  options?: unknown[];
};

export type ProposeTemplateRepeatableGroup = {
  id: string;
  label: string;
  fields?: ProposeTemplateField[];
  children?: ProposeTemplateRepeatableGroup[];
};

export type ProposeTemplateInitialProduct = {
  tableId: string;
  sourceRow?: number;
  groupIds?: Array<string | number> | Record<string, string | number>;
  values: Record<string, unknown>;
};

export type ProposeTemplateTable = {
  id: string;
  label: string;
  fieldIds?: string[];
  groupPath?: string[];
};

export type ProposeTemplateFormDefinition = {
  id: number;
  version: number;
  headerFields?: ProposeTemplateField[];
  productFields?: ProposeTemplateField[];
  initialProducts?: ProposeTemplateInitialProduct[];
  repeatableGroups?: ProposeTemplateRepeatableGroup[];
  tables?: ProposeTemplateTable[];
};

export type ProposeTemplatePreviewSheet = {
  name: string;
  maxRow: number;
  maxColumn: number;
  cells: Record<string, unknown>;
  formulas?: Record<string, string>;
  merges?: string[];
};

export type ProposeTemplatePreview = {
  templateId: number;
  name: string;
  fileName: string;
  sheets: ProposeTemplatePreviewSheet[];
};

export type ProposeTemplateCellEdit = {
  sheetName: string;
  address: string;
  value: string;
};

export type ProposeTemplateSubmission = {
  order_name: string;
  product_code: string[];
  headers: Record<string, unknown>;
  representative_id?: string;
  representative_name?: string;
  deadline?: string;
};

export type ProposeTemplateSubmissionCreateResult = {
  success: boolean;
  detail: string;
  submission_id: number;
};

export type ProposeSubmissionGoogleSheetStatus = {
  submission_id: number;
  job_id?: string;
  success: boolean;
  status: string;
  detail: string;
  url: string | null;
};

export type ProposeGoogleSheetRole = "edit" | "view" | "comment";

export type ProposeGoogleSheetPermission = {
  email: string;
  role: string;
};

export type ProposeGoogleSheet = {
  id: string;
  template_id?: number;
  name: string;
  url: string;
  mimeType: string;
  createdAt?: string;
  updatedAt?: string;
  sharedWith?: string[];
  sharedRole?: ProposeGoogleSheetRole;
  permissions?: ProposeGoogleSheetPermission[];
};

export type ProposeGoogleSheetsPage = {
  items: ProposeGoogleSheet[];
  pagination: {
    page: number;
    page_size: number;
    total: number;
    total_pages: number;
  };
};

export type ProposeGoogleSheetPermissionUpdateResult = {
  success: boolean;
  detail: string;
  spreadsheet_id?: string;
  permissions?: Array<{
    email: string;
    role: string;
  }>;
};

export type ProposeTemplateGoogleSheetSyncResult = {
  success: boolean;
  detail: string;
};

export type ProposeTemplateRenameResult = {
  success: boolean;
  detail: string;
};

const recommendClient = axios.create({
  baseURL: env.recommendApiUrl,
  timeout: env.apiTimeoutMs,
  headers: { Accept: "application/json" },
});

recommendClient.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) config.headers.set("Authorization", `Bearer ${token}`);
  return config;
});

const normalizeApiKeys = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(normalizeApiKeys);
  if (!value || typeof value !== "object") return value;
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).map(([key, nestedValue]) => [
      key.replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase()),
      normalizeApiKeys(nestedValue),
    ]),
  );
};

export const getProposeTemplateErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as { error?: unknown } | undefined;
    if (typeof body?.error === "string" && body.error.trim()) {
      return body.error;
    }
  }
  return normalizeApiError(error).message;
};

export const listProposeTemplates = async (): Promise<ProposeTemplate[]> =>
  (await recommendClient.get<ProposeTemplate[]>("/templates")).data;

export const createProposeGoogleSheet = async (payload: {
  emails: string[];
}): Promise<ProposeGoogleSheet> =>
  (await recommendClient.post<ProposeGoogleSheet>("/google-sheets", payload)).data;

export const createProposeTemplateFromGoogleSheet = async (payload: {
  spreadsheet_id: string;
  guidance: string;
}): Promise<unknown> =>
  (await recommendClient.post("/templates/from-google-sheet", payload)).data;

export const syncProposeTemplateGoogleSheet = async (
  templateId: number,
): Promise<ProposeTemplateGoogleSheetSyncResult> =>
  (
    await recommendClient.put<ProposeTemplateGoogleSheetSyncResult>(
      `/templates/${templateId}/google-sheet`,
    )
  ).data;

export const listProposeGoogleSheets = async (params: {
  page: number;
  page_size: number;
  search?: string;
}): Promise<ProposeGoogleSheetsPage> =>
  (await recommendClient.get<ProposeGoogleSheetsPage>("/google-sheets", { params })).data;

export const updateProposeGoogleSheetPermissions = async (
  spreadsheetId: string,
  payload: {
    emails: string[];
    role: ProposeGoogleSheetRole;
  },
): Promise<ProposeGoogleSheetPermissionUpdateResult> =>
  (
    await recommendClient.put<ProposeGoogleSheetPermissionUpdateResult>(
      `/google-sheets/${encodeURIComponent(spreadsheetId)}/permissions`,
      payload,
    )
  ).data;

export const uploadProposeTemplate = async (
  file: File,
  name: string,
): Promise<ProposeTemplate> => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("name", name);
  return (
    await recommendClient.post<ProposeTemplate>("/templates/upload", formData)
  ).data;
};

export const getProposeTemplateStatus = async (
  id: number,
): Promise<ProposeTemplateStatusResponse> =>
  (await recommendClient.get<ProposeTemplateStatusResponse>(`/templates/${id}/status`))
    .data;

export const renameProposeTemplate = async (
  templateId: number,
  name: string,
): Promise<ProposeTemplateRenameResult> =>
  (
    await recommendClient.put<ProposeTemplateRenameResult>(`/templates/${templateId}`, {
      name,
    })
  ).data;

export const getProposeTemplate = async (
  id: number,
): Promise<ProposeTemplateFormDefinition> => {
  const response = await recommendClient.get<unknown>(`/templates/${id}`);
  return normalizeApiKeys(response.data) as ProposeTemplateFormDefinition;
};

export const submitProposeTemplate = async (
  templateId: number,
  submission: ProposeTemplateSubmission,
): Promise<ProposeTemplateSubmissionCreateResult> =>
  (
    await recommendClient.post<ProposeTemplateSubmissionCreateResult>(
      `/submissions/${templateId}`,
      submission,
    )
  ).data;

export const createProposeSubmissionGoogleSheet = async (
  submissionId: number,
): Promise<unknown> =>
  (await recommendClient.post(`/submissions/${submissionId}/google-sheet`)).data;

export const getProposeSubmissionGoogleSheetStatus = async (
  submissionId: number,
): Promise<ProposeSubmissionGoogleSheetStatus> =>
  (
    await recommendClient.get<ProposeSubmissionGoogleSheetStatus>(
      `/submissions/${submissionId}/google-sheet/status`,
    )
  ).data;

export const listProposeTemplateSubmissions = async (): Promise<unknown> =>
  (await recommendClient.get("/submissions")).data;

export const listSellerProposeTemplateSubmissions = async (
  sellerCode: string,
  limit = 50,
  offset = 0,
): Promise<unknown> =>
  (
    await recommendClient.get(`/sellers/${encodeURIComponent(sellerCode)}/submissions`, {
      params: { limit, offset },
    })
  ).data;

export const listSellerFinishedProposeTemplateSubmissions = async (
  sellerCode: string,
  limit = 50,
  offset = 0,
): Promise<unknown> =>
  (
    await recommendClient.get(
      `/sellers/${encodeURIComponent(sellerCode)}/submissions/finished`,
      { params: { limit, offset } },
    )
  ).data;

export type SellerProposeTemplateSubmissionDetail = {
  id: number;
  template_id?: number;
  template_name?: string;
  order_name?: string;
  status?: number | string;
  updated_at?: string;
  submission_information?: Record<string, unknown>;
  payload?: unknown;
  payload_json?: unknown;
  is_leader?: boolean;
};

export const getSellerProposeTemplateSubmission = async (
  sellerCode: string,
  submissionId: number,
): Promise<SellerProposeTemplateSubmissionDetail> =>
  (
    await recommendClient.get<SellerProposeTemplateSubmissionDetail>(
      `/sellers/${encodeURIComponent(sellerCode)}/submissions/${submissionId}`,
    )
  ).data;

export type SellerSubmissionProduct = {
  id: string;
  tableId: string;
  groupIds: Record<string, string | number>;
  values: Record<string, unknown>;
};

/** Save a seller's repeatable order data when that seller is a product leader.
 * @param sellerCode Source user ID of the seller making the edit.
 * @param submissionId ID of the order being edited.
 * @param products Complete repeatable product entries to save.
 * @returns The updated submission response from the backend.
 */
export const saveSellerSubmissionRepeatableData = async (
  sellerCode: string,
  submissionId: number,
  products: SellerSubmissionProduct[],
): Promise<unknown> =>
  (
    await recommendClient.put(`/submissions/${submissionId}/repeatable-data`, {
      products,
    }, { params: { seller_code: sellerCode } })
  ).data;

/** Submit a seller's order with any final product or cell edits.
 * @param sellerCode Source user ID of the seller submitting the order.
 * @param submissionId ID of the order being submitted.
 * @param edits Optional final product and cell values.
 * @returns The finished submission response from the backend.
 */
export const submitSellerProposeTemplateSubmission = async (
  sellerCode: string,
  submissionId: number,
  edits?: { products?: SellerSubmissionProduct[]; cells?: ProposeTemplateCellEdit[] },
): Promise<unknown> =>
  (
    await recommendClient.post(`/submissions/${submissionId}/submit`, edits ?? {}, {
      params: { seller_code: sellerCode },
    })
  ).data;

export const deleteProposeTemplateSubmission = async (id: number): Promise<void> => {
  await recommendClient.delete(`/submissions/${id}`);
};

export const getProposeTemplatePreview = async (
  id: number,
): Promise<ProposeTemplatePreview> =>
  (await recommendClient.get<ProposeTemplatePreview>(`/templates/${id}/preview`))
    .data;

export const getProposeTemplateSubmissionPreview = async (
  id: number,
): Promise<ProposeTemplatePreview> =>
  (await recommendClient.get<ProposeTemplatePreview>(`/submissions/${id}/preview`))
    .data;

export const downloadProposeTemplateSubmissionExcel = async (
  id: number,
): Promise<{ blob: Blob; fileName?: string }> => {
  const response = await recommendClient.get<Blob>(`/submissions/${id}/excel`, {
    responseType: "blob",
  });
  const disposition = response.headers["content-disposition"] as string | undefined;
  const encodedName = disposition?.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  const plainName = disposition?.match(/filename="?([^";]+)"?/i)?.[1];
  let fileName = plainName;
  if (encodedName) {
    try {
      fileName = decodeURIComponent(encodedName);
    } catch {
      fileName = encodedName;
    }
  }
  return { blob: response.data, fileName };
};

export const updateProposeTemplateCells = async (
  id: number,
  cells: ProposeTemplateCellEdit[],
): Promise<ProposeTemplatePreview> =>
  (
    await recommendClient.put<ProposeTemplatePreview>(
      `/templates/${id}/cells`,
      { cells },
    )
  ).data;

export const updateProposeTemplateSubmissionCells = async (
  id: number,
  cells: ProposeTemplateCellEdit[],
): Promise<ProposeTemplatePreview> =>
  (
    await recommendClient.put<ProposeTemplatePreview>(
      `/submissions/${id}/cells`,
      { cells },
    )
  ).data;

export const deleteProposeTemplate = async (
  id: number,
): Promise<{ templateId: number; deleted: boolean }> =>
  (await recommendClient.delete<{ templateId: number; deleted: boolean }>(`/templates/${id}`))
    .data;
